import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Car, 
  Users, 
  FileText, 
  Wrench, 
  CreditCard,
  Home,
  X,
  CarTaxiFront,
  AlertTriangle,
  Settings,
  Server,
  DollarSign,
  Calendar,
  Satellite,
  Lock,
  UserCog,
  Eye,
  Clock,
  TrendingUp,
  ChevronRight,
  Package,
  BarChart3,
  Upload,
  MessageCircle
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { apiService as api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export const AdminSidebar = ({ isOpen, onClose }) => {
  const [reservasPendentes, setReservasPendentes] = useState(0);
  const [atendimentosNaoLidos, setAtendimentosNaoLidos] = useState(0);
  const { user } = useAuth();

  const carregarContadorReservas = async () => {
    try {
      const response = await api.api.get('/reservas/pendentes/count');
      setReservasPendentes(response.data?.count || 0);
    } catch (error) {
      console.error('Erro ao carregar reservas:', error);
    }
  };

  const carregarContadorAtendimentos = async () => {
    try {
      const response = await api.api.get('/admin/atendimentos');
      setAtendimentosNaoLidos(response.data?.total_nao_lidos || 0);
    } catch (error) {/* silent */}
  };

  useEffect(() => {
    if (user?.tipo === 'super_admin') {
      carregarContadorReservas();
      const interval = setInterval(carregarContadorReservas, 30000);
      return () => clearInterval(interval);    }
  }, [user]);

  useEffect(() => {
    // Atendimentos disponíveis pra todos os admins
    carregarContadorAtendimentos();
    const interval = setInterval(carregarContadorAtendimentos, 15000);
    return () => clearInterval(interval);
  }, []);

  const restrictedPaths = [
    '/admin/reservas',
    '/admin/cobrancas',
    '/admin/multas',
    '/admin/whatsapp',
    '/admin/configuracoes',
    '/admin/configuracoes-avancadas',
    '/admin/usuarios',
    '/admin/gordurinha',  // Apenas super_admin
    '/admin/estoque',     // Apenas super_admin
    '/admin/brasilsat',    // GPS/Alertas - Apenas super_admin
    '/admin/motos-bloqueadas',  // Bloqueio Brasil Sat - Apenas super_admin
    '/admin/diagnostico-pagamentos',  // Diagnóstico - Apenas super_admin
    '/admin/clientes-ativos',  // Sincronizado com app cliente - Apenas super_admin
    '/admin/atendimentos'  // Chat do App Cliente - Apenas super_admin
  ];

  const menuGroups = [
    {
      title: 'Principal',
      items: [
        { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
        { path: '/admin/reservas', icon: Calendar, label: 'Reservas Online', badge: reservasPendentes },
        { path: '/admin/fila-espera', icon: Clock, label: 'Fila de Espera' },
      ]
    },
    {
      title: 'Gestão',
      items: [
        { path: '/admin/motos', icon: Car, label: 'Motos' },
        { path: '/admin/clientes', icon: Users, label: 'Clientes' },
        { path: '/admin/clientes-ativos', icon: Users, label: 'Clientes Ativos' },
        { path: '/admin/locacoes', icon: FileText, label: 'Locações' },
        { path: '/admin/motos-alugadas', icon: CarTaxiFront, label: 'Motos Alugadas' },
      ]
    },
    {
      title: 'Manutenção',
      items: [
        { path: '/admin/manutencao', icon: Wrench, label: 'Registro de Manutenções' },
        { path: '/admin/brasilsat', icon: Satellite, label: 'Telemetria / GPS' },
        { path: '/admin/motos-bloqueadas', icon: Lock, label: 'Motos Bloqueadas' },
        { path: '/admin/estoque', icon: Package, label: 'Estoque de Peças' },
      ]
    },
    {
      title: 'Financeiro',
      items: [
        { path: '/admin/pagamentos', icon: CreditCard, label: 'Pagamentos' },
        { path: '/admin/comprovantes-app', icon: Upload, label: 'Comprovantes do App' },
        { path: '/admin/receitas', icon: DollarSign, label: 'Receitas' },
        { path: '/admin/fechamento', icon: BarChart3, label: 'Fechamento Mensal' },
        { path: '/admin/gordurinha', icon: TrendingUp, label: 'Gordurinha' },
        { path: '/admin/cobrancas', icon: AlertTriangle, label: 'Débitos' },
        { path: '/admin/diagnostico-pagamentos', icon: Wrench, label: 'Diagnóstico Pagamentos' },
      ]
    },
    {
      title: 'Sistema',
      items: [
        { path: '/admin/atendimentos', icon: MessageCircle, label: 'Atendimentos', badge: atendimentosNaoLidos > 0 ? atendimentosNaoLidos : null },
        { path: '/admin/configuracoes', icon: Settings, label: 'Configurações' },
        { path: '/admin/usuarios', icon: UserCog, label: 'Usuários' },
      ]
    }
  ];

  const filteredGroups = menuGroups.map(group => ({
    ...group,
    items: user?.tipo === 'sub_admin'
      ? group.items.filter(item => !restrictedPaths.includes(item.path))
      : group.items
  })).filter(group => group.items.length > 0);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`
          fixed inset-y-0 left-0 w-72 lg:w-64 
          bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950
          flex flex-col
          transform transition-all duration-300 ease-out
          ${isOpen ? 'translate-x-0 z-50' : '-translate-x-full z-0'}
          lg:relative lg:translate-x-0 lg:z-auto
          shadow-2xl
        `}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* Logo Header */}
        <div className="p-5 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_bdae70c3-fcfc-4eba-a30e-28680e4c45c3/artifacts/uf9q7vx0_IMG_2719.jpeg"
                alt="Desloca"
                className="h-10 w-auto rounded-lg"
              />
              <div>
                <span className="text-white font-bold text-lg">Desloca</span>
                <p className="text-gray-500 text-xs">Gestão de Motos</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Badge Sub-Admin */}
          {user?.tipo === 'sub_admin' && (
            <div className="mt-4 flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 px-3 py-2 rounded-lg text-xs font-medium">
              <Eye className="h-4 w-4" />
              <span>Modo Leitura</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent">
          {filteredGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="mb-6">
              <p className="px-3 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {group.title}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.exact}
                    onClick={() => window.innerWidth < 1024 && onClose()}
                    className={({ isActive }) =>
                      `group flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all duration-200
                      ${isActive
                        ? 'bg-green-500 text-white shadow-lg shadow-green-500/25'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800/80'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <div className="flex items-center gap-3">
                          <item.icon className={`h-5 w-5 ${isActive ? '' : 'group-hover:scale-110'} transition-transform`} />
                          <span className="font-medium">{item.label}</span>
                        </div>
                        {item.badge > 0 ? (
                          <Badge className="bg-red-500 text-white text-xs px-2">{item.badge}</Badge>
                        ) : (
                          <ChevronRight className={`h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity ${isActive ? 'opacity-100' : ''}`} />
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-gray-800">
          <NavLink
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 text-gray-400 hover:text-white hover:bg-gray-800 transition-all rounded-xl text-sm font-medium"
          >
            <Home className="h-5 w-5" />
            <span>Voltar ao Site</span>
          </NavLink>
        </div>
      </aside>
    </>
  );
};
